import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Particle from "../Particle";

import imgGauges from "../../Assets/Projects/netvis/gauges.png";
import imgAppModal from "../../Assets/Projects/netvis/appmodal.png";
import imgGlobe from "../../Assets/Projects/netvis/globe.png";
import imgGlobeMenu from "../../Assets/Projects/netvis/globe_menu.png";
import imgTraffic from "../../Assets/Projects/netvis/traffic.png";
import imgUtility from "../../Assets/Projects/netvis/utility.png";
import imgSpeedtest from "../../Assets/Projects/netvis/speedtest.png";

const ss = { borderRadius: "8px", width: "100%", marginBottom: "10px" };
const label = { color: "rgb(155 126 172)", marginBottom: "6px", marginTop: "14px" };

function Section({ title, children }) {
  return (
    <>
      <Row style={{ justifyContent: "center", paddingTop: "30px" }}>
        <Col md={10}>
          <h4 style={{ color: "white", borderBottom: "1px solid rgb(100 60 140)", paddingBottom: "8px" }}>
            {title}
          </h4>
        </Col>
      </Row>
      {children}
    </>
  );
}

function Shot({ src, caption, md = 10 }) {
  return (
    <Row style={{ justifyContent: "center", paddingBottom: "6px" }}>
      <Col md={md}>
        <p style={label}>{caption}</p>
        <img src={src} alt={caption} className="img-fluid" style={ss} />
      </Col>
    </Row>
  );
}

function ShotPair({ left, right, captionL, captionR }) {
  return (
    <Row style={{ justifyContent: "center", paddingBottom: "6px" }}>
      <Col md={5}>
        <p style={label}>{captionL}</p>
        <img src={left} alt={captionL} className="img-fluid" style={ss} />
      </Col>
      <Col md={5}>
        <p style={label}>{captionR}</p>
        <img src={right} alt={captionR} className="img-fluid" style={ss} />
      </Col>
    </Row>
  );
}

function NetVis() {
  return (
    <Container fluid className="project-section">
      <Container>
        <h1 className="project-heading">
          <strong className="purple">NetVis</strong>
        </h1>
        <p style={{ color: "white", textAlign: "center" }}>
          NetVis is a real-time network monitor and control panel for Windows. It shows what every
          app on your machine is doing on the network - how much it's moving, where it's going, and
          how healthy that connection is - and lets you act on it: throttle an app's bandwidth, cut
          it off, or kill it outright. It's a single-dependency Python backend with a custom
          canvas-rendered frontend, packaged into a standalone .exe.
        </p>

        <Row style={{ justifyContent: "center", paddingTop: "20px", paddingBottom: "10px", color: "white" }}>
          <Col md={10} className="project-card">
            <h4>What It Does</h4>
            <br />
            <p style={{ color: "white", textAlign: "left" }}>
              <ul>
                <li><b>Service Gauges</b> - a live capsule per app showing download/upload speed,
                  latency, jitter, packet loss, and connection/destination/port counts, with the
                  app's real icon pulled from its executable.</li>
                <li><b>Per-app QoS</b> - independent up/down bandwidth throttle sliders, a hard
                  block (kill-switch), and a process kill - all enforced in user space by a
                  WinDivert driver, so limits vanish the instant NetVis closes.</li>
                <li><b>App Page</b> - click any app for a full breakdown: every stat, QoS controls,
                  traffic-type and country breakdowns, and a filterable connection table with
                  per-destination bandwidth.</li>
                <li><b>Live Connection Globe</b> - a rotating earth that pins your location and draws
                  animated arcs to every geo-located destination. Click a node to see which apps
                  are talking to it.</li>
                <li><b>Traffic Breakdown</b> - a GlassWire-style split of session traffic across
                  Apps, Hosts (reverse-DNS), Traffic Type (HTTPS/DNS/STUN/...), and Countries.</li>
                <li><b>Network Utility</b> - WAN/LAN info (external IP, ISP, gateway, DNS,
                  interfaces), diagnostics (ping, traceroute, DNS, route, ARP, netstat), one-click
                  fixes (flush DNS, release/renew, winsock/TCP-IP reset), and live speed tests via
                  Cloudflare, Fast.com, or Speedtest.net.</li>
                <li><b>Privacy-aware</b> - NetVis hides its own connections, and active latency
                  probing can be switched off entirely so it makes zero outbound connections to
                  your apps' servers.</li>
              </ul>
            </p>

            <h4>Built With</h4>
            <br />
            <p style={{ color: "white", textAlign: "left" }}>
              <ul>
                <li>Python standard-library HTTP server + <b>psutil</b> - one dependency, no web framework</li>
                <li><b>WinDivert</b> (pydivert) for bidirectional per-app packet shaping, blocking, and a passive bandwidth sniffer</li>
                <li>Windows perf counters, <code>netsh</code>, firewall, and routing for QoS fallback and diagnostics</li>
                <li>A single self-contained HTML page with hand-written Canvas rendering - the globe, charts, and gauges use no chart libraries</li>
                <li>ip-api GeoIP + Natural Earth coastlines for the globe and country breakdowns</li>
                <li><b>pywebview</b> + <b>PyInstaller</b> to ship it as a standalone Windows .exe</li>
              </ul>
            </p>

            <h4>Why I Built It</h4>
            <br />
            <p style={{ color: "white", textAlign: "left" }}>
              I wanted to actually see and control my machine's network the way tools like GlassWire
              or NetLimiter do - but as something I fully understood and could extend. It started as
              a simple "show me traffic in/out" dashboard and grew into a full per-app monitor with
              QoS, a geographic visualization, and a diagnostics suite. Every piece - the packet
              shaper, the globe projection, the speed tests - is built from the ground up.
            </p>
          </Col>
        </Row>

        <Section title="Service Gauges">
          <Shot src={imgGauges} caption="One capsule per app - live download/upload, latency, jitter, loss, and connection counts, with per-app up/down throttle sliders plus Block and Kill" />
        </Section>

        <Section title="App Page">
          <Shot src={imgAppModal} caption="Click any app for the full page - eight live metrics, QoS controls, traffic-type and country breakdowns, and a filterable connection table with per-destination bandwidth" />
        </Section>

        <Section title="Live Connection Globe">
          <Shot src={imgGlobe} caption="A rotating earth centered on your location, with animated arcs to every geo-located destination (dot size = connection count)" />
          <Shot src={imgGlobeMenu} caption="Click a connection node to see which apps are talking to that destination - selecting one opens its app page" />
        </Section>

        <Section title="Traffic Breakdown">
          <Shot src={imgTraffic} caption="GlassWire-style session traffic split across Apps, Hosts (with reverse-DNS and country flags), Traffic Type, and Countries, with a WAN/LAN summary" />
        </Section>

        <Section title="Network Utility & Diagnostics">
          <Shot src={imgUtility} caption="WAN/LAN info, every network interface, one-click fixes, and a built-in speed test (Cloudflare / Fast.com / Speedtest.net)" />
          <Shot src={imgSpeedtest} caption="Live speed-test results measured through the chosen provider, alongside inline diagnostic command output" />
        </Section>

        <Particle />
      </Container>
    </Container>
  );
}

export default NetVis;
